"use client";

import { useEffect } from "react";
import { onMessage, type Messaging } from "firebase/messaging";
import { getFirebaseMessaging } from "@/lib/firebase/config";
import { logger } from "@/lib/logger";

type NotificationParams = {
  title: string;
  body?: string;
  icon?: string;
};

/**
 * Register the FCM service worker and listen for foreground messages.
 * @returns Helper for showing a browser notification manually
 */
export const useNotification = () => {
  /**
   * Show a browser notification when permission has already been granted.
   * @returns Created notification or `null` when notification permission is missing
   */
  const createNotification = (params: NotificationParams) => {
    if (Notification.permission !== "granted") {
      return null;
    }

    return new Notification(params.title, {
      body: params.body,
      icon: params.icon,
    });
  };

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    void navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .catch(logger.error);
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isMounted = true;

    const setupMessaging = async () => {
      const messaging: Messaging | undefined = await getFirebaseMessaging();
      if (!isMounted || !messaging) {
        return;
      }

      unsubscribe = onMessage(messaging, (payload) => {
        logger.log("[FCM] onMessage payload:", payload);
        const data = payload.notification;
        const notification = createNotification({
          title: data?.title ?? "",
          body: data?.body,
          icon: data?.icon,
        });

        if (!notification) {
          return;
        }

        notification.onclick = () => {
          const url = payload.data?.url || payload.fcmOptions?.link;
          if (url) {
            window.location.href = url;
          }
        };
      });
    };

    void setupMessaging();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, []);

  return { createNotification };
};
